import { View } from 'backbone';
import _ from 'underscore';
import { getLogger } from '../logging/logger';
import { wait } from '../util/utilities';

export const Events = Object.freeze({
	updated: 'updated',
});

/**
 * @typedef {import('backbone').ViewOptions} ViewOptions
 * @typedef {import('consola').Consola} Consola
 * @typedef {import('./index').DecoratorElement} DecoratorElement
 *
 * Base class for decorators that extend Backbone View with update functionality.
 * Provides automatic DOM monitoring, resize handling, and update lifecycle management.
 *
 * @class
 * @extends View<Backbone.Model, DecoratorElement>
 */
export default class BaseDecorator extends View {
	/**
	 * Gets the class name for logging purposes.
	 * @returns {string} The class name
	 */
	get CN() {
		return this.constructor.name || BaseDecorator.name;
	}

	/**
	 * Gets the logger instance for this decorator.
	 * @returns {Consola} Logger instance
	 */
	get logger() {
		return getLogger(this.CN);
	}

	/**
	 * Gets the CSS class name used to indicate when updates are complete.
	 * @returns {string} The updated class name
	 */
	get updatedClassName() {
		return 'decorated';
	}

	/**
	 * Initializes the decorator and sets up DOM monitoring and resize handling.
	 */
	initialize() {
		this.logger.log(`[${this.initialize.name}]`);

		this.interval = setInterval(this.checkDom.bind(this), 250);
		this.startUpdate = _.debounce(this.startUpdate.bind(this), 100);

		window.addEventListener('resize', this.startUpdate);
		this.scheduleUpdate();
	}

	/**
	 * Schedules an update to occur after the next animation frame.
	 * Ensures the DOM is ready before starting the update process.
	 * Allows ovrriding to wait for specific conditions before updating.
	 * @returns {Promise<any>} Promise that resolves when the update is scheduled
	 */
	scheduleUpdate() {
		this.logger.log(`[${this.scheduleUpdate.name}]`);

		// Just wait for an animation frame to ensure the DOM is ready
		return wait().then(this.startUpdate.bind(this));
	}

	/**
	 * Starts the update process by removing the decorated class, performing the update,
	 * and then adding the updated class back.
	 * @returns {Promise<any>} Promise that resolves when the update is complete
	 */
	async startUpdate() {
		this.logger.log(`[${this.startUpdate.name}]`);

		this.$el.removeClass(this.updatedClassName); // lack of css class hides children via associated styles
		await wait();
		await this.update();
		this.$el.addClass(this.updatedClassName);
		this.trigger(Events.updated, this);
	}

	/**
	 * Abstract method that should be overridden in subclasses to perform specific updates.
	 * For example, resizing elements or updating styles based on the current state.
	 * @abstract
	 * @returns {Promise<any>} Promise that resolves when the update is complete
	 * @throws {Error} Throws error if not implemented in subclass
	 */
	update() {
		this.logger.log(`[${this.update.name}]`);

		// This method should be overridden in subclasses to perform specific updates
		// For example, resizing elements or updating styles based on the current state
		this.logger.error(
			`[${this.update.name}]`,
			'This method should be overridden in subclasses.',
		);
		return Promise.reject(new Error('update method must be implemented in subclass'));
	}

	/**
	 * Checks if the element is still in the DOM and destroys the decorator if not.
	 * Called periodically via setInterval to clean up orphaned decorators.
	 */
	checkDom() {
		this.logger.debug(`[${this.checkDom.name}]`);

		if (!document.contains(this.el)) {
			this.destroy();
		}
	}

	/**
	 * Cleans up the decorator by removing event listeners, clearing intervals,
	 * and removing references from the DOM element.
	 */
	destroy() {
		this.logger.log(`[${this.destroy.name}]`);

		window.removeEventListener('resize', this.startUpdate);
		clearInterval(this.interval);
		delete this.el[this.CN];
	}
}
